// ./utils/timeUtils.ts

/**
 * Formats a time string to ensure it includes seconds.
 * @param time - A time string, e.g., "08:30" or "08:30:00".
 * @returns A time string in "HH:mm:ss" format.
 */
export const formatTime = (time: string): string => {
  if (!time) return '00:00:00'
  return time.length === 5 ? `${time}:00` : time
}

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

const minutesToTime = (minutes: number): string => {
  const hrs = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0')
  const mins = (minutes % 60).toString().padStart(2, '0')
  return `${hrs}:${mins}`
}

/**
 * Generates time select options between `start` and `end` (inclusive), every `step` minutes.
 * If `selectedDate` is today, past times are disabled.
 */
export function generateTimeOptions(start: string, end: string, step: number, selectedDate?: string) {
  const startMinutes = timeToMinutes(start)
  const endMinutes = timeToMinutes(end)
  const options: Array<{ value: string; label: string; disabled?: boolean }> = []

  // Check if selected date is today
  const isToday = selectedDate === new Date().toISOString().split('T')[0]
  const currentTime = new Date()
  const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes()

  for (let current = startMinutes; current <= endMinutes; current += step) {
    const timeStr = minutesToTime(current)
    options.push({
      value: timeStr,
      label: timeStr,
      disabled: isToday && current <= currentMinutes,
    })
  }

  return options
}

const DEFAULT_SCROLL_TIME = '12:00'

/**
 * MenuProps for time selects: when no value is selected, the list opens centered
 * on noon (or the first available time after noon) instead of midnight.
 * When a value is selected, MUI already scrolls to it.
 */
export const timeSelectMenuProps = {
  TransitionProps: {
    // MUI's Popover applies its positioning (max-height, top) right AFTER onEntering, and iOS Safari
    // resets/ignores a scrollTop set before that layout settles, so we defer to the next frame.
    // Positions come from offsetTop/clientHeight (layout values): getBoundingClientRect is affected by
    // the Grow transition's scale() and gives wrong offsets while the menu is opening.
    onEntering: (paper: HTMLElement) => {
      if (paper.querySelector('.Mui-selected')) return

      const scrollToNoon = () => {
        const items = Array.from(paper.querySelectorAll<HTMLElement>('[role="option"][data-value]'))
        const target = items.find(
          (item) => (item.dataset.value ?? '') >= DEFAULT_SCROLL_TIME && item.getAttribute('aria-disabled') !== 'true',
        )
        if (!target) return

        // Sum offsetTop up the offsetParent chain until we reach the scrolling paper.
        let targetTop = 0
        let node: HTMLElement | null = target
        while (node && node !== paper) {
          targetTop += node.offsetTop
          node = node.offsetParent as HTMLElement | null
        }
        // If the paper isn't in the chain (not positioned), offsetTop is already relative to the list.
        if (!node) targetTop = target.offsetTop

        paper.scrollTop = targetTop - paper.clientHeight / 2 + target.offsetHeight / 2
      }

      requestAnimationFrame(() => {
        scrollToNoon()
        // iOS can still re-layout once more; re-apply on the following frame.
        requestAnimationFrame(scrollToNoon)
      })
    },
  },
}
